import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export type SharedCalendarColorConfig = {
    backgroundColorToken?: string;
    borderColorToken?: string;
    textColorToken?: string;
};

export type SharedCalendarLegendItem = {
    type: string;
    label: string;
    description?: string;
};

export type SharedCalendarAllDayStyleConfig =
    | {
          mode: "fixedGreen";
      }
    | {
          mode: "inheritStatus";
      };

/** Copy and primary action for the schedule list empty state. */
export type SharedCalendarScheduleEmptyStateConfig = {
    createButtonLabel?: string;
    title?: string;
    titleWithFilters?: string;
    description?: string;
    descriptionWithFilters?: string;
    /** Workspace create form used when the empty-state primary button is clicked. */
    createFormId?: Uuid;
};

export type ResolvedCalendarScheduleEmptyState = {
    createButtonLabel: string;
    title: string;
    titleWithFilters: string;
    description: string;
    descriptionWithFilters: string;
    createFormId?: Uuid;
};

export type SharedCalendarViewConfig = {
    eventTypeLabelMap?: Record<string, string>;
    legendItems?: Array<SharedCalendarLegendItem>;
    eventTypeColorMap?: Record<string, SharedCalendarColorConfig>;
    templateStrokeColorMap?: Record<string, string>;
    allDayStyle?: SharedCalendarAllDayStyleConfig;
    scheduleEmptyState?: SharedCalendarScheduleEmptyStateConfig;
};

const sharedCalendarColorConfigValidator: zod.ZodType<SharedCalendarColorConfig> = zod.strictObject({
    backgroundColorToken: zod.string().optional(),
    borderColorToken: zod.string().optional(),
    textColorToken: zod.string().optional(),
});

const sharedCalendarLegendItemValidator: zod.ZodType<SharedCalendarLegendItem> = zod.strictObject({
    type: zod.string(),
    label: zod.string(),
    description: zod.string().optional(),
});

const sharedCalendarAllDayStyleConfigValidator: zod.ZodType<SharedCalendarAllDayStyleConfig> = zod.discriminatedUnion("mode", [
    zod.strictObject({mode: zod.literal("fixedGreen")}),
    zod.strictObject({mode: zod.literal("inheritStatus")}),
]);

const sharedCalendarScheduleEmptyStateConfigValidator: zod.ZodType<SharedCalendarScheduleEmptyStateConfig> = zod.strictObject({
    createButtonLabel: zod.string().optional(),
    title: zod.string().optional(),
    titleWithFilters: zod.string().optional(),
    description: zod.string().optional(),
    descriptionWithFilters: zod.string().optional(),
    createFormId: uuidZodValidator.optional(),
});

export const sharedCalendarViewConfigValidator = zod.strictObject({
    eventTypeLabelMap: zod.record(zod.string(), zod.string()).optional(),
    legendItems: sharedCalendarLegendItemValidator.array().optional(),
    eventTypeColorMap: zod.record(zod.string(), sharedCalendarColorConfigValidator).optional(),
    templateStrokeColorMap: zod.record(zod.string(), zod.string()).optional(),
    allDayStyle: sharedCalendarAllDayStyleConfigValidator.optional(),
    scheduleEmptyState: sharedCalendarScheduleEmptyStateConfigValidator.optional(),
}) satisfies zod.ZodType<SharedCalendarViewConfig>;
